/* eslint-disable max-len */
// src/components/Teacher/ModerationInbox.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/firebase';
import { useAuth } from '../../contexts/AuthContext';
 
const C = {
  primary:          '#6a37d4',
  primaryLight:     '#ae8dff',
  primaryContainer: '#ede8ff',
  secondary:        '#006947',
  secondaryContainer:'#d1fae5',
  tertiary:         '#6f5900',
  tertiaryContainer:'#fed01b',
  error:            '#b41340',
  errorContainer:   '#ffd9df',
  surface:          '#f3f6ff',
  surfaceLow:       '#eaf1ff',
  surfaceHigh:      '#d6e3f9',
  surfaceLowest:    '#ffffff',
  onBackground:     '#252f3c',
  onSurface:        '#252f3c',
  onSurfaceVariant: '#525c6a',
  outlineVariant:   '#a4aebe',
};
 
interface QueuedStory {
  id: string;
  storyTitle: string;
  authorName: string;
  storyPreview: string;
  aiFlags: { category: string; detail: string }[];
  classId?: string;
  className?: string;
  submittedAt: any;
  status: string;
}
 
const FLAG_ICONS: Record<string, string> = {
  bullying:      '💬',
  inappropriate: '⚠️',
  contact:       '🔒',
};
 
interface Props {
  onCountChange?: (count: number) => void;
}
 
const ModerationInbox: React.FC<Props> = ({ onCountChange }) => {
  const { currentUser } = useAuth();
  const [stories, setStories]           = useState<QueuedStory[]>([]);
  const [loading, setLoading]           = useState(true);
  const [actioning, setActioning]       = useState<string | null>(null);
  const [rejectModal, setRejectModal]   = useState<{ storyId: string; title: string } | null>(null);
  const [feedback, setFeedback]         = useState('');
  const [expanded, setExpanded]         = useState(true);
 
  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      const snap = await getDocs(
        query(
          collection(db, 'moderationQueue'),
          where('teacherId', '==', currentUser.uid),
          where('status', '==', 'pending'),
        )
      );
      const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() } as QueuedStory));
      setStories(loaded);
      onCountChange?.(loaded.length);
    } catch (e) {
      console.error('ModerationInbox load error:', e);
    } finally {
      setLoading(false);
    }
  }, [currentUser, onCountChange]);
 
  useEffect(() => { load(); }, [load]);
 
  const remove = (storyId: string) => {
    setStories(prev => {
      const next = prev.filter(s => s.id !== storyId);
      onCountChange?.(next.length);
      return next;
    });
  };
 
  const handleApprove = async (storyId: string) => {
    setActioning(storyId);
    try {
      const fn = httpsCallable(getFunctions(), 'approveModeratedStory');
      await fn({ storyId });
      remove(storyId);
    } catch (e) {
      console.error('Approve error:', e);
    } finally {
      setActioning(null);
    }
  };
 
  const handleReject = async () => {
    if (!rejectModal) return;
    setActioning(rejectModal.storyId);
    try {
      const fn = httpsCallable(getFunctions(), 'rejectModeratedStory');
      await fn({ storyId: rejectModal.storyId, feedback });
 
      // Write to teacherFeedback so student sees it in their editor FeedbackPanel
      const userDoc = await getDoc(doc(db, 'users', currentUser!.uid));
      const teacherName = userDoc.data()?.displayName || 'Your Teacher';
      await setDoc(doc(db, 'teacherFeedback', rejectModal.storyId), {
        status: 'returned',
        teacherName,
        publicComment: feedback || 'Your story needs some changes before it can be published.',
        suggestions: [],
        reactions: {},
        returnedAt: new Date(),
      }, { merge: true });

      // Update studentAssignments so TodoList shows 'returned'
const queueSnap = await getDoc(doc(db, 'moderationQueue', rejectModal.storyId));
const queueData = queueSnap.data();
if (queueData?.userId && queueData?.assignmentId) {
    const statusRef = doc(db, 'studentAssignments',
        `${queueData.userId}_${queueData.assignmentId}`);
    const statusDoc = await getDoc(statusRef);
    if (statusDoc.exists()) {
        await setDoc(statusRef, { status: 'returned' }, { merge: true });
    }
}

 
      remove(rejectModal.storyId);
      setRejectModal(null);
      setFeedback('');
    } catch (e) {
      console.error('Reject error:', e);
    } finally {
      setActioning(null);
    }
  };
  
 
  if (loading || stories.length === 0) return null;
 
  return (
    <>
      <div style={{
        background: C.surfaceLowest, borderRadius: '20px',
        border: `2px solid ${C.error}30`,
        boxShadow: `0 4px 20px ${C.error}10`,
        marginBottom: '32px', overflow: 'hidden',
      }}>
        {/* Header row */}
        <div
          onClick={() => setExpanded(!expanded)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '20px 24px', cursor: 'pointer',
            background: `${C.errorContainer}60`,
            borderBottom: expanded ? `1px solid ${C.surfaceHigh}` : 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '1.4rem' }}>🔍</span>
            <div>
              <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.05rem', color: C.onBackground }}>
                Pending Review
              </div>
              <div style={{ fontSize: '0.8rem', color: C.onSurfaceVariant, marginTop: '2px' }}>
                {stories.length} {stories.length === 1 ? 'story' : 'stories'} flagged by AI — your approval publishes them
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ background: C.error, color: '#fff', borderRadius: '9999px', padding: '3px 10px', fontSize: '0.78rem', fontWeight: '800' }}>
              {stories.length}
            </span>
            <span style={{ color: C.onSurfaceVariant, fontSize: '0.85rem', transform: expanded ? 'rotate(180deg)' : 'rotate(0)', display: 'inline-block', transition: 'transform .2s' }}>▼</span>
          </div>
        </div>
 
        {/* Story list */}
        {expanded && (
          <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {stories.map(story => (
              <div key={story.id} style={{ background: C.surface, borderRadius: '16px', border: `1px solid ${C.surfaceHigh}`, padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Story info */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div>
                    <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '1rem', color: C.onBackground, marginBottom: '3px' }}>
                      {story.storyTitle}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: C.onSurfaceVariant }}>
                      by {story.authorName}
                      {story.className && (
                        <span style={{ marginLeft: '8px', background: C.primaryContainer, color: C.primary, borderRadius: '9999px', padding: '1px 8px', fontSize: '0.75rem', fontWeight: '700' }}>
                          {story.className}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: C.onSurfaceVariant, flexShrink: 0 }}>
                    {story.submittedAt?.toDate?.()?.toLocaleDateString?.() || ''}
                  </div>
                </div>
 
                {/* Preview */}
                {story.storyPreview && (
                  <div style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, fontFamily: 'Georgia, serif', lineHeight: '1.6', background: C.surfaceLow, borderRadius: '10px', padding: '12px 14px', fontStyle: 'italic' }}>
                    "{story.storyPreview}…"
                  </div>
                )}
 
                {/* AI flags */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {story.aiFlags?.map((flag, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', background: `${C.tertiaryContainer}70`, border: `1.5px solid ${C.tertiary}30`, borderRadius: '10px', padding: '8px 12px', fontSize: '0.8rem', color: C.onSurface, flex: '1 1 200px' }}>
                      <span>{FLAG_ICONS[flag.category] || '⚠️'}</span>
                      <span>{flag.detail}</span>
                    </div>
                  ))}
                </div>
 
                {/* Actions */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => handleApprove(story.id)}
                    disabled={actioning === story.id}
                    style={{ flex: 1, padding: '11px', background: `linear-gradient(135deg, ${C.secondary}, #00a86b)`, color: '#fff', border: 'none', borderRadius: '9999px', fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.875rem', cursor: 'pointer', opacity: actioning === story.id ? 0.6 : 1 }}
                  >
                    {actioning === story.id ? 'Publishing…' : '✅ Approve & Publish'}
                  </button>
                  <button
                    onClick={() => setRejectModal({ storyId: story.id, title: story.storyTitle })}
                    disabled={actioning === story.id}
                    style={{ flex: 1, padding: '11px', background: C.surfaceLow, color: C.onSurfaceVariant, border: `1.5px solid ${C.surfaceHigh}`, borderRadius: '9999px', fontFamily: "'Epilogue', sans-serif", fontWeight: '700', fontSize: '0.875rem', cursor: 'pointer' }}
                  >
                    ↩️ Return to Student
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
 
      {/* Reject / return modal */}
      {rejectModal && (
        <div onClick={() => setRejectModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(37,47,60,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px', backdropFilter: 'blur(4px)' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.surfaceLowest, borderRadius: '20px', padding: '32px', maxWidth: '440px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.18)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <div style={{ fontSize: '2rem', marginBottom: '12px' }}>↩️</div>
            <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.15rem', color: C.onBackground, margin: '0 0 6px 0' }}>
              Return to student
            </h3>
            <p style={{ fontSize: '0.875rem', color: C.onSurfaceVariant, margin: '0 0 20px 0', lineHeight: '1.6' }}>
              <strong style={{ color: C.onBackground }}>{rejectModal.title}</strong> will not be published. Add feedback for the student — they'll see it in their editor's Feedback panel.
            </p>
            <textarea
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              placeholder="What should the student fix? (e.g. 'Please remove the romantic scene in Chapter 1 — keep it age-appropriate!')"
              rows={4}
              style={{ width: '100%', padding: '14px', borderRadius: '12px', border: `1.5px solid ${C.surfaceHigh}`, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '0.9rem', color: C.onSurface, resize: 'vertical', outline: 'none', boxSizing: 'border-box', marginBottom: '20px' }}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => { setRejectModal(null); setFeedback(''); }} style={{ flex: 1, padding: '12px', background: C.surfaceLowest, color: C.onSurfaceVariant, border: `1.5px solid ${C.outlineVariant}`, borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}>
                Cancel
              </button>
              <button onClick={handleReject} disabled={actioning === rejectModal.storyId} style={{ flex: 1, padding: '12px', background: C.primary, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", opacity: actioning ? 0.6 : 1 }}>
                {actioning ? 'Sending…' : 'Send & Return'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
 
export default ModerationInbox;